/**
 * Field Update Indicator Component
 *
 * Standalone component to show visual indicators when specific fields
 * have been updated from external sources (Firebase, system, etc.).
 *
 * @author Claude Code
 * @date 2025-09-22
 */

import React, { useContext, useMemo, useState, useEffect } from 'react';
import { Box, Tooltip, Fade, IconButton, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { FieldUpdateContext } from '../../contexts/FieldUpdateContext';
import { FieldUpdateEvent, FieldUpdateContextValue } from '../../types';

// Animation keyframes
const rippleEffect = keyframes`
  0% {
    transform: scale(0);
    opacity: 1;
  }
  100% {
    transform: scale(4);
    opacity: 0;
  }
`;

const fadeInScale = keyframes`
  from {
    transform: scale(0.8);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`;

// Styled components
const IndicatorContainer = styled(Box)<{ source: 'firebase' | 'user' | 'system' | 'ai' }>(
  ({ theme, source }) => ({
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: source === 'firebase'
      ? theme.palette.info.main
      : source === 'user'
      ? theme.palette.success.main
      : source === 'ai'
      ? theme.palette.secondary.main
      : theme.palette.warning.main,
    animation: `${fadeInScale} 0.3s ease-out`,
    cursor: 'pointer',
    '&:hover': {
      transform: 'scale(1.1)',
      transition: 'transform 0.2s ease-out'
    }
  })
);

const RippleAnimation = styled('div')<{ source: 'firebase' | 'user' | 'system' | 'ai' }>(
  ({ theme, source }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    border: `2px solid ${source === 'firebase'
      ? theme.palette.info.main
      : source === 'user'
      ? theme.palette.success.main
      : source === 'ai'
      ? theme.palette.secondary.main
      : theme.palette.warning.main}`,
    animation: `${rippleEffect} 1.5s ease-out infinite`,
    pointerEvents: 'none'
  })
);

const IndicatorIcon = styled('span')(({ theme }) => ({
  fontSize: '10px',
  color: theme.palette.common.white,
  fontWeight: 'bold'
}));

export interface FieldUpdateIndicatorProps {
  fieldPath: string;
  showTooltip?: boolean;
  autoHide?: boolean;
  autoHideDelay?: number;
  className?: string;
  onClick?: (update: FieldUpdateEvent) => void;
}

/**
 * Visual indicator for field updates
 */
export const FieldUpdateIndicator: React.FC<FieldUpdateIndicatorProps> = ({
  fieldPath,
  showTooltip = true,
  autoHide = true,
  autoHideDelay = 5000,
  className,
  onClick
}) => {
  const fieldUpdateContext = useContext(FieldUpdateContext) as FieldUpdateContextValue | null;
  const [isVisible, setIsVisible] = useState(false);

  // Get the most recent update for this field
  const recentUpdate = useMemo(() => {
    if (!fieldUpdateContext) return null;

    const updates = fieldUpdateContext.fieldUpdates
      .filter(update => update.path === fieldPath)
      .sort((a, b) => b.timestamp - a.timestamp);

    return updates[0] || null;
  }, [fieldUpdateContext?.fieldUpdates, fieldPath]);

  // Auto-hide logic
  useEffect(() => {
    if (recentUpdate && autoHide) {
      setIsVisible(true);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, autoHideDelay);

      return () => clearTimeout(timer);
    } else if (recentUpdate) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [recentUpdate, autoHide, autoHideDelay]);

  // Don't render if no recent update
  if (!recentUpdate || !isVisible) {
    return null;
  }

  // Format timestamp
  const formatTimestamp = (timestamp: number): string => {
    const now = Date.now();
    const diff = now - timestamp;

    if (diff < 1000) return 'только что';
    if (diff < 60000) return `${Math.floor(diff / 1000)} сек назад`;
    if (diff < 3600000) return `${Math.floor(diff / 60000)} мин назад`;
    return new Date(timestamp).toLocaleTimeString();
  };

  // Get source details
  const getSourceDetails = (source: string) => {
    switch (source) {
      case 'firebase':
        return {
          icon: '🔄',
          label: 'Обновлено из Firebase',
          description: 'Данные обновлены внешним источником'
        };
      case 'user':
        return {
          icon: '👤',
          label: 'Изменено пользователем',
          description: 'Поле было изменено пользователем'
        };
      case 'system':
        return {
          icon: '⚙️',
          label: 'Системное обновление',
          description: 'Обновлено системой автоматически'
        };
      default:
        return {
          icon: '📝',
          label: 'Обновлено',
          description: 'Поле было обновлено'
        };
    }
  };

  const sourceDetails = getSourceDetails(recentUpdate.source);

  // Handle click
  const handleClick = () => {
    if (onClick) {
      onClick(recentUpdate);
    }
  };

  // Tooltip content
  const tooltipContent = (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {sourceDetails.label}
      </Typography>
      <Typography variant="body2" gutterBottom>
        <strong>Поле:</strong> {fieldPath}
      </Typography>
      <Typography variant="body2" gutterBottom>
        <strong>Время:</strong> {formatTimestamp(recentUpdate.timestamp)}
      </Typography>
      {recentUpdate.metadata?.description && (
        <Typography variant="body2">
          <strong>Описание:</strong> {recentUpdate.metadata.description}
        </Typography>
      )}
    </Box>
  );

  const indicator = (
    <Fade in={isVisible} timeout={300}>
      <IndicatorContainer
        source={recentUpdate.source}
        onClick={handleClick}
        className={className}
      >
        <RippleAnimation source={recentUpdate.source} />
        <IndicatorIcon>
          {sourceDetails.icon}
        </IndicatorIcon>
      </IndicatorContainer>
    </Fade>
  );

  if (showTooltip) {
    return (
      <Tooltip
        title={tooltipContent}
        placement="top"
        arrow
        enterDelay={500}
      >
        {indicator}
      </Tooltip>
    );
  }

  return indicator;
};

/**
 * Multiple field update indicator for sections
 */
export interface SectionUpdateIndicatorProps {
  sectionPath: string;
  maxIndicators?: number;
  showCount?: boolean;
  className?: string;
}

export const SectionUpdateIndicator: React.FC<SectionUpdateIndicatorProps> = ({
  sectionPath,
  maxIndicators = 3,
  showCount = true,
  className
}) => {
  const fieldUpdateContext = useContext(FieldUpdateContext) as FieldUpdateContextValue | null;

  // Get recent updates for this section
  const sectionUpdates = useMemo(() => {
    if (!fieldUpdateContext) return [];

    const now = Date.now();
    const oneMinuteAgo = now - (60 * 1000);

    return fieldUpdateContext.fieldUpdates
      .filter(update =>
        update.path.startsWith(sectionPath) &&
        update.timestamp > oneMinuteAgo
      )
      .sort((a, b) => b.timestamp - a.timestamp);
  }, [fieldUpdateContext?.fieldUpdates, sectionPath]);

  if (sectionUpdates.length === 0) {
    return null;
  }

  const displayUpdates = sectionUpdates.slice(0, maxIndicators);
  const remainingCount = sectionUpdates.length - displayUpdates.length;

  return (
    <Box display="flex" alignItems="center" gap={0.5} className={className}>
      {displayUpdates.map((update, index) => (
        <FieldUpdateIndicator
          key={update.id}
          fieldPath={update.path}
          autoHide={false}
        />
      ))}
      {remainingCount > 0 && showCount && (
        <Typography variant="caption" color="primary">
          +{remainingCount}
        </Typography>
      )}
    </Box>
  );
};

export default FieldUpdateIndicator;